import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'app-custom-search',
  templateUrl: './custom-search.component.html',
  styleUrls: ['./custom-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSearchComponent),
      multi: true
    }
  ],
  animations: [
    // Trigger (botão colapsado) — fade out rápido quando o chip expande.
    trigger('chipTriggerFade', [
      transition(':leave', [
        animate('100ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 0, transform: 'scale(0.85)' })),
      ]),
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.85)' }),
        animate('150ms 80ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
    // Container expandido — cresce de 36px (largura do trigger) para 280px
    // com fade-in do conteúdo. Cubic-bezier = `$ease-out` do design system.
    trigger('chipExpand', [
      transition(':enter', [
        style({ width: '36px', opacity: 0 }),
        animate('220ms 60ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                style({ width: '280px', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('160ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                style({ width: '36px', opacity: 0 })),
      ]),
    ]),
  ],
})
export class CustomSearchComponent implements ControlValueAccessor, OnInit {

  ngOnInit(): void {
    if (this.chipMode) {
      this.updateNarrowState();
    }
    // alwaysOpen + viewport largo → arranca expandido. Em viewport estreito,
    // permanece colapsado e o user expande via click/atalho como dropdown
    // flutuante.
    if (this.alwaysOpen && (!this.isNarrow || this.forceOpenNarrow)) {
      this.expanded = true;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (!this.chipMode) {
      return;
    }
    const wasNarrow = this.isNarrow;
    this.updateNarrowState();

    if (this.alwaysOpen) {
      if (this.isNarrow && !wasNarrow) {
        // Viewport encolheu — colapsa se input vazio.
        if (!this.model) {
          this.expanded = false;
        }
      } else if (!this.isNarrow && wasNarrow) {
        // Viewport voltou a ser largo — reabre.
        this.expanded = true;
      }
    }
  }

  private updateNarrowState(): void {
    if (typeof window !== 'undefined') {
      this.isNarrow = window.innerWidth < CustomSearchComponent.NARROW_THRESHOLD;
    }
  }

  // Em alwaysOpen + viewport estreito, o chip-expandido deve flutuar
  // (dropdown). Em alwaysOpen + viewport largo, fica inline no header.
  get isFloating(): boolean {
    return this.alwaysOpen && this.isNarrow && !this.forceOpenNarrow && this.expanded;
  }

  @Input() placeholder: String = this.translateService.instant('search-bar-placeholder');

  // Quando true, renderiza como "chip-expand": botão pequeno com ícone de lupa
  // que ao clicar expande para o input completo. Esc/blur com texto vazio
  // colapsa de volta. Default false mantém o input sempre visível (legado).
  @Input() chipMode = false;

  // Quando true (junto com chipMode), o chip fica permanentemente expandido —
  // sem botão colapsado, sem auto-colapsar em blur. Usado quando o filtro fica
  // centralizado na página como elemento principal.
  @Input() alwaysOpen = false;

  // Quando true, `alwaysOpen` também vale em viewport estreito (mobile).
  // Default false preserva o comportamento chip-que-vira-dropdown dos 10
  // callers admin existentes.
  @Input() forceOpenNarrow = false;

  @ViewChild('chipInput') chipInput?: ElementRef<HTMLInputElement>;

  model: string = '';
  expanded = false;

  // Hint do atalho — tecla `/` (sem modificador). Mesmo padrão pra todos os SOs.
  readonly shortcutHint = '/';

  // Threshold abaixo do qual o chip colapsa mesmo em alwaysOpen (espaço
  // apertado pra mostrar header inteiro). 1024px = típico "small desktop".
  private static readonly NARROW_THRESHOLD = 1024;

  // Estado de viewport — quando true, `alwaysOpen` se comporta como modo
  // toggle normal (colapsa, expande como dropdown flutuante).
  isNarrow = false;

  onChangeFn: any = () => {};
  onTouchedFn: any = () => {};

  constructor(
    public translateService: TranslateService,
  ) {}

  writeValue(value: string): void {
    this.model = value;
    // Se vier valor não vazio, fica expandido pra mostrar o texto.
    if (this.chipMode && value) {
      this.expanded = true;
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  onChange(value: string) {
    this.model = value;
    this.onChangeFn(value);
  }

  expandChip(): void {
    this.expanded = true;
    // Foca o input no próximo tick (após o *ngIf renderizar).
    setTimeout(() => this.chipInput?.nativeElement?.focus(), 0);
  }

  collapseChip(): void {
    // Em alwaysOpen + viewport largo, nunca colapsa. Em viewport estreito,
    // colapsa normalmente (dropdown comportment).
    if (this.alwaysOpen && (!this.isNarrow || this.forceOpenNarrow)) {
      return;
    }
    // Só colapsa se input está vazio — preserva o estado expandido enquanto
    // há filtro ativo (visível pro usuário saber que está filtrando).
    if (!this.model) {
      this.expanded = false;
    }
  }

  clearAndCollapse(): void {
    this.onChange('');
    // alwaysOpen + viewport largo: só limpa. Demais casos: limpa + colapsa.
    if (!(this.alwaysOpen && (!this.isNarrow || this.forceOpenNarrow))) {
      this.expanded = false;
    }
  }

  onChipKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.clearAndCollapse();
    }
  }

  // Tecla `/` abre/foca o chip — apenas em chipMode.
  // Atalho "/" foca o filtro chip (padrão GitHub/Gmail). Ativo SOMENTE
  // nas páginas que usam `chipMode`; outras páginas e qualquer campo de
  // digitação preservam o "/" literal. Modificadores (Ctrl/Cmd/Alt)
  // também passam direto — não colidem com Ctrl+K (busca global) etc.
  @HostListener('document:keydown', ['$event'])
  onDocKey(event: KeyboardEvent): void {
    if (!this.chipMode || event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const active = document.activeElement as HTMLElement | null;
    const tag = active?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || active?.isContentEditable) {
      return;
    }
    event.preventDefault();
    if (!this.expanded) {
      // Expande o chip; o `expandChip()` já chama `focus()` no input quando
      // ele entra no DOM, então não precisamos refocar daqui.
      this.expandChip();
    } else {
      this.chipInput?.nativeElement?.focus();
      this.chipInput?.nativeElement?.select();
    }
  }
}
